/**
 * Autocomplete dịch vụ điều trị dùng chung.
 * Menu: tên, dòng dưới mã và số tiền. Ô đã chọn: `Tên (số tiền)`.
 */
import type { ReactNode } from 'react';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import { useCurrency } from '@/shared/context/CurrencyContext';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@/shared/ui';
import type { AutocompleteProps } from '@/shared/ui';
import { mergeSx } from '@/shared/ui/utils/mergeSx';
import { typography } from '@/shared/theme/tokens';

export interface TreatmentServiceOption {
  _id?: string;
  id?: string;
  name: string;
  code?: string;
  price?: number;
}

const serviceIds = (item: TreatmentServiceOption): string[] =>
  [item.id, item._id].filter((id): id is string => Boolean(id));

const servicesMatch = (left: TreatmentServiceOption, right: TreatmentServiceOption): boolean => {
  const leftIds = serviceIds(left);
  const rightIds = serviceIds(right);
  return leftIds.some((id) => rightIds.includes(id));
};

const selectedAutocompleteSx = {
  '&:not(:hover):not(.Mui-focused) .MuiAutocomplete-endAdornment': { display: 'none' },
  '&:not(:hover):not(.Mui-focused) .MuiOutlinedInput-root': { pr: '8px !important' },
} as const;

const hasSelectedValue = (value: unknown, multiple: boolean): boolean => {
  if (multiple) return Array.isArray(value) && value.length > 0;
  return value != null;
};

export const filterTreatmentServices = <T extends TreatmentServiceOption>(
  options: T[],
  state: FilterOptionsState<T>,
): T[] => {
  const query = state.inputValue.trim().toLocaleLowerCase('vi');
  if (!query) return options;
  return options.filter((item) => {
    const name = item.name.toLocaleLowerCase('vi');
    const code = (item.code ?? '').toLocaleLowerCase('vi');
    return name.includes(query) || code.includes(query);
  });
};

export type TreatmentServiceAutocompleteProps<
  T extends TreatmentServiceOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
> = Omit<
  AutocompleteProps<T, Multiple, DisableClearable, false>,
  'options' | 'getOptionLabel' | 'renderOption' | 'isOptionEqualToValue' | 'renderInput'
> & {
  options: T[];
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: ReactNode;
};

function TreatmentServiceAutocomplete<
  T extends TreatmentServiceOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
>({
  options,
  label,
  placeholder,
  error,
  helperText,
  multiple,
  value,
  loading,
  forcePopupIcon,
  sx,
  filterOptions,
  variant,
  ...rest
}: TreatmentServiceAutocompleteProps<T, Multiple, DisableClearable>) {
  const { formatCurrency } = useCurrency();
  const selected = hasSelectedValue(value, Boolean(multiple));
  const inputLabel = (option: T): string => `${option.name} (${formatCurrency(option.price)})`;

  return (
    <Autocomplete
      {...rest}
      multiple={multiple}
      options={options}
      value={value}
      loading={loading}
      variant={variant}
      forcePopupIcon={forcePopupIcon ?? (selected ? false : 'auto')}
      sx={mergeSx(selected ? selectedAutocompleteSx : undefined, sx)}
      filterOptions={filterOptions ?? filterTreatmentServices}
      getOptionLabel={inputLabel}
      isOptionEqualToValue={servicesMatch}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            component="li"
            key={key}
            {...optionProps}
            sx={{
              '&&': {
                alignItems: 'flex-start',
                height: 'auto',
                whiteSpace: 'normal',
              },
            }}
          >
            <Box sx={{ minWidth: 0, py: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: typography.weight.medium }}>
                {option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {option.code} · {formatCurrency(option.price)}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={multiple && selected ? undefined : placeholder}
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={variant === 'compact' ? 16 : 18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

export default TreatmentServiceAutocomplete;
